import React, { useState } from "react";
import { Button, Flex } from "@jsq/ds/core";
import { ConfirmOnBehalfModal } from "./modals/ConfirmOnBehalfModal";
import { usePostBtsComment } from "./usePostBtsComment";
import type { StageDocumentOptions } from "../../hooks/useDocuments";
import type { TaskStepDetail } from "../../types";

interface OnBehalfActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  onActionClick: (action: string, payload?: Record<string, unknown>) => void;
  /** JSM issue key used to post the audit note as a BTS comment alongside the action. */
  issueKey?: string;
  stageDocument?: (file: File, options: StageDocumentOptions) => Promise<number>;
  deleteUploadedDocument?: (documentId: number) => Promise<void>;
}

// Fund-admin (Canopy) action bar for the external-review surface
// (ExecutionState.WAITING). The FA records the external party's (GP's) decision
// on their behalf rather than approving directly — the confirm modal captures
// the mandatory audit note before the approve_on_behalf mutation fires.
export const OnBehalfActionBar: React.FC<OnBehalfActionBarProps> = ({
  task,
  actionLoading,
  onActionClick,
  issueKey,
  stageDocument,
  deleteUploadedDocument,
}) => {
  const postComment = usePostBtsComment(issueKey, task);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset the modal when navigating to a different task so it never carries
  // over. Adjusting state during render (rather than in an effect) is React's
  // recommended pattern for resetting state on a prop change.
  const [prevTaskId, setPrevTaskId] = useState(task.id);
  if (prevTaskId !== task.id) {
    setPrevTaskId(task.id);
    setConfirmOpen(false);
  }

  const onBehalfAction = task.availableActions.find((a) => a.action === "approve_on_behalf");

  const handleConfirm = (note: string) => {
    setConfirmOpen(false);
    onActionClick("approve_on_behalf", { note });
    postComment(note, false);
  };

  const boundStageDocument = stageDocument
    ? (file: File) =>
        stageDocument(file, {
          taskId: task.id,
          purpose: "OFF_PLATFORM_CONFIRMATION",
          isExternal: true,
        })
    : undefined;

  if (!onBehalfAction) return null;

  return (
    <>
      <Flex gap="8px" alignItems="center">
        <Button
          variant="primary"
          disabled={actionLoading}
          onClick={() => setConfirmOpen(true)}
          data-testid="on-behalf-approve"
        >
          {onBehalfAction.label ?? "Approve on Client's Behalf"}
        </Button>
      </Flex>
      <ConfirmOnBehalfModal
        isOpen={confirmOpen}
        loading={actionLoading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        stageDocument={boundStageDocument}
        deleteUploadedDocument={deleteUploadedDocument}
      />
    </>
  );
};

export default OnBehalfActionBar;
